import React from 'react';
import { level as busLevel, micLevel } from './ariaAudioBus';

// One persistent drawing loop. State changes ease into the existing frame;
// speech deformation comes only from measured audio, never a fake envelope.
export default function TalkingBall({speaking=false,listening=false,thinking=false,muted=false,onToggleMute,size=340,theme}) {
  const canvas=React.useRef(null);
  const live=React.useRef({});
  live.current={speaking,listening,thinking,muted,theme};
  React.useEffect(()=>{
    const cv=canvas.current;
    if(!cv) return;
    const g=cv.getContext('2d');
    if(!g) return;
    const dpr=Math.min(2,window.devicePixelRatio || 1);
    cv.width=Math.round(size*dpr);cv.height=Math.round(size*dpr);g.setTransform(dpr,0,0,dpr,0,0);
    const reduced=window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame=0,last=0,phase=0,energy=0,spin=0;
    let color=[93,158,193];
    const draw=now=>{
      const dt=Math.min(.05,Math.max(.001,(now-(last || now-16))/1000));last=now;
      const p=live.current;
      const state=p.speaking?'speaking':p.thinking?'thinking':p.muted?'muted':p.listening?'listening':'idle';
      const target=state==='speaking'?busLevel():state==='listening'?micLevel():0;
      energy+=(Math.min(1,Math.max(0,target))-energy)*(1-Math.exp(-dt/(target>energy?.045:.14)));
      const wanted=state==='speaking'?[175,134,255]:state==='thinking'?[229,194,117]:state==='listening'?[91,205,244]:[113,132,153];
      color=color.map((v,i)=>v+(wanted[i]-v)*(1-Math.exp(-dt/.18)));
      const rgb=color.map(Math.round).join(',');
      if(!reduced.matches){phase+=dt*.65;spin+=dt*(state==='thinking'?1.5:.12);}
      const c=size/2,r=size*.335,amp=reduced.matches?0:energy;
      g.clearRect(0,0,size,size);
      const halo=g.createRadialGradient(c,c,r*.65,c,c,r*1.4);
      halo.addColorStop(0,`rgba(${rgb},0)`);halo.addColorStop(.55,`rgba(${rgb},${.08+energy*.16})`);halo.addColorStop(1,`rgba(${rgb},0)`);
      g.fillStyle=halo;g.fillRect(0,0,size,size);
      const core=g.createRadialGradient(c-r*.3,c-r*.4,0,c,c,r);
      core.addColorStop(0,p.theme==='day'?'#e5eef4':'#1e2c3b');core.addColorStop(.7,p.theme==='day'?'#dce6ee':'#0c1420');core.addColorStop(1,p.theme==='day'?'#c5d6e3':'#060b12');
      g.fillStyle=core;g.beginPath();g.arc(c,c,r,0,Math.PI*2);g.fill();
      // A restrained rim adds depth without hundreds of flickering dots.
      g.strokeStyle=`rgba(${rgb},.22)`;g.lineWidth=1;g.beginPath();g.arc(c,c,r*1.04,0,Math.PI*2);g.stroke();
      for(let layer=0;layer<3;layer++){
        g.beginPath();
        for(let i=0;i<=160;i++){
          const a=i/160*Math.PI*2;
          const shape=(Math.sin(a*3+phase+layer*.8)*.6+Math.sin(a*5-phase*.7)*.25);
          const radius=r*(.93-layer*.065)+amp*r*.105*shape;
          const x=c+Math.cos(a)*radius,y=c+Math.sin(a)*radius;
          if(i===0)g.moveTo(x,y);else g.lineTo(x,y);
        }
        g.closePath();g.strokeStyle=`rgba(${rgb},${(.85-layer*.25)*(state==='muted'?.3:1)})`;
        g.lineWidth=Math.max(1,size*(layer===0?.009:.004));g.shadowColor=`rgba(${rgb},.55)`;g.shadowBlur=size*.035;g.stroke();g.shadowBlur=0;
      }
      if(state==='thinking'){
        g.beginPath();g.arc(c,c,r*.93,spin,spin+Math.PI*.65);g.strokeStyle=`rgba(${rgb},.95)`;g.lineWidth=Math.max(2,size*.012);g.lineCap='round';g.stroke();
      }
      // Central bars respond to real amplitude; silence leaves a calm mark.
      if(state==='speaking'||state==='listening'){
        for(let i=-2;i<=2;i++){
          const h=size*(.018+amp*(.075-.013*Math.abs(i)));
          g.beginPath();g.moveTo(c+i*size*.042,c-h);g.lineTo(c+i*size*.042,c+h);g.lineWidth=Math.max(2,size*.012);g.lineCap='round';g.strokeStyle=`rgba(${rgb},${.45+energy*.5})`;g.stroke();
        }
      }
      if(state==='muted'){
        g.strokeStyle='#b3becb';g.lineWidth=Math.max(1.5,size*.012);g.lineCap='round';
        g.beginPath();g.arc(c,c-size*.02,size*.035,0,Math.PI);g.moveTo(c,c+size*.015);g.lineTo(c,c+size*.075);g.moveTo(c-size*.05,c+size*.075);g.lineTo(c+size*.05,c+size*.075);g.moveTo(c-size*.075,c-size*.075);g.lineTo(c+size*.075,c+size*.075);g.stroke();
      }
      frame=requestAnimationFrame(draw);
    };
    frame=requestAnimationFrame(draw);
    return()=>cancelAnimationFrame(frame);
  },[size]);
  const label=speaking?'Speaking':thinking?'Thinking':muted?'Muted':listening?'Listening':'Ready';
  const clickable=typeof onToggleMute==='function';
  return <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:2,pointerEvents:'none'}}>
    <canvas ref={canvas} role={clickable?'button':'img'} tabIndex={clickable?0:undefined} aria-label={clickable?`${label}. ${muted?'Unmute':'Mute'} microphone`:label} onClick={clickable?onToggleMute:undefined} onKeyDown={clickable?e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();onToggleMute();}}:undefined} style={{width:size,height:size,display:'block',pointerEvents:clickable?'auto':'none',cursor:clickable?'pointer':'default',borderRadius:'50%'}}/>
    <span style={{fontSize:11,fontWeight:600,letterSpacing:'.08em',color:theme==='day'?'#405366':'#bac8d7',minHeight:16}}>{label}</span>
  </div>;
}
